"use client";

import type { CodeLanguageGlobeBlock, Media } from "@mw/shared/payload-types";
import type { Key } from "react-aria-components";

import { clsx } from "clsx";
import { useState, useMemo } from "react";
import { Select, Button, SelectValue } from "react-aria-components";

import LazyGlobe from "@/app/_ui/components/core/Globe/LazyGlobe";
import type { LanguageAudioSample } from "@/app/_ui/components/core/Globe/LazyGlobe";
import { ListBox } from "@/app/_ui/components/core/ListBox";
import { getAudioUrl } from "@/app/_utilities/payload";
import { Block } from "../Block";
import { useTranslations } from "next-intl";

// Language options with their codes, labels, and locale for flag
// Order matches Globe.tsx languages array
const LANGUAGE_OPTIONS = [
  { code: "eng", label: "English", locale: "en" },
  { code: "cmn", label: "Chinese", locale: "zh" },
  { code: "spa", label: "Spanish", locale: "es" },
  { code: "fra", label: "French", locale: "fr" },
  { code: "por", label: "Portuguese", locale: "pt" },
  { code: "deu", label: "German", locale: "de" },
  { code: "jpn", label: "Japanese", locale: "ja" },
  { code: "ita", label: "Italian", locale: "it" },
  { code: "hin", label: "Hindi", locale: "hi" },
] as const;

// Custom dropdown component that looks like a code token with full keyboard support
function LanguageDropdown({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: typeof LANGUAGE_OPTIONS;
}) {
  const items = useMemo(
    () =>
      options.map(opt => ({
        id: opt.code,
        textValue: opt.label,
        subLabel: opt.code,
      })),
    [options],
  );

  return (
    <Select
      selectedKey={value}
      onSelectionChange={(key: Key | null) => {
        if (key !== null) onChange(key as string);
      }}
      aria-label='Language code'
      className='tw-inline-block tw-align-baseline'
    >
      <Button
        className={({ isFocusVisible }) =>
          clsx(
            "tw-inline-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-rounded tw-border tw-border-black tw-bg-black/10 hover:tw-bg-black/20 tw-transition-colors tw-font-mono tw-text-inherit tw-cursor-pointer",
            isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
          )
        }
        style={{ color: "#a31515" }}
      >
        <SelectValue>{({ selectedText }) => <>&quot;{value}&quot;</>}</SelectValue>
      </Button>
      <ListBox items={items} offset={4} style={{ minWidth: "140px" }} />
    </Select>
  );
}

// Code card wrapper with title
function CodeCardWrapper({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className='tw-bg-white tw-aspect-square tw-flex tw-flex-col tw-border tw-border-black/10 tw-shadow-sm tw-rounded-3xl'>
      {/* Title header */}
      {title && (
        <div className='tw-px-6 md:tw-px-8 tw-pt-6 md:tw-pt-8 tw-pb-4'>
          <p className='tw-text-sm tw-text-black/60 tw-pb-4 tw-border-b tw-border-dashed tw-border-black/15'>{title}</p>
        </div>
      )}
      {/* Code content */}
      <div className={clsx("tw-px-6 md:tw-px-8 tw-pb-6 md:tw-pb-8 tw-flex-1", !title && "tw-pt-6 md:tw-pt-8")}>
        {children}
      </div>
    </div>
  );
}

// Code card for transcription variant with title and embedded dropdown
function TranscriptionCodeCard({
  title,
  selectedLanguage,
  onLanguageChange,
}: {
  title?: string;
  selectedLanguage: string;
  onLanguageChange: (code: string) => void;
}) {
  const t = useTranslations("ClientKeys.TTS");
  return (
    <CodeCardWrapper title={title}>
      {/* eslint-disable i18next/no-literal-string */}
      <pre className='tw-text-xs tw-leading-6 tw-font-mono tw-whitespace-pre-wrap tw-break-words tw-text-black'>
        <code>
          <span style={{ color: "#F41A2F" }}>import</span>
          <span>{" { "}</span>
          <span>ElevenLabsClient</span>
          <span>{" } "}</span>
          <span style={{ color: "#F41A2F" }}>from</span>
          <span style={{ color: "#8A8A8A" }}>{' "@elevenlabs/elevenlabs-js"'}</span>
          <span>;</span>
          {"\n\n"}
          <span style={{ color: "#F41A2F" }}>const</span>
          <span> elevenlabs = </span>
          <span style={{ color: "#F41A2F" }}>new</span>
          <span> ElevenLabsClient</span>
          <span>{"({\n\t"}</span>
          <span>apiKey: </span>
          <span style={{ color: "#8A8A8A" }}>{'"<your_api_key>"'}</span>
          <span>{"\n});"}</span>
          {"\n"}
          <span style={{ color: "#F41A2F" }}>const</span>
          <span> response = </span>
          <span style={{ color: "#F41A2F" }}>await</span>
          <span> </span>
          <span style={{ color: "#0A59D2" }}>fetch</span>
          <span>(</span>
          {"\n  "}
          <span style={{ color: "#8A8A8A" }}>
            {'"https://storage.googleapis.com/eleven-public-cdn/audio/marketing/nicole.mp3"'}
          </span>
          {"\n"}
          <span>);</span>
          {"\n"}
          <span style={{ color: "#F41A2F" }}>const</span>
          <span> audioBlob = </span>
          <span style={{ color: "#F41A2F" }}>new</span>
          <span> </span>
          <span style={{ color: "#0A59D2" }}>Blob</span>
          <span>{"(["}</span>
          <span style={{ color: "#F41A2F" }}>await</span>
          <span> response.</span>
          <span style={{ color: "#0A59D2" }}>arrayBuffer</span>
          <span>{"()], { "}</span>
          <span>type: </span>
          <span style={{ color: "#8A8A8A" }}>{'"audio/mp3"'}</span>
          <span>{" });"}</span>
          {"\n\n"}
          <span style={{ color: "#F41A2F" }}>const</span>
          <span> transcription = </span>
          <span style={{ color: "#F41A2F" }}>await</span>
          <span>{"\t"}elevenlabs</span>
          {"\n\t"}
          <span>.speechToText.convert</span>
          <span>({"{"}</span>
          {"\n\t  "}
          <span>file: audioBlob,</span>
          {"\n\t  "}
          <span>modelId: </span>
          <span style={{ color: "#8A8A8A" }}>{'"scribe_v2"'}</span>
          <span>,</span>
          {"\n\t  "}
          <span>tagAudioEvents: </span>
          <span style={{ color: "#0A59D2" }}>true</span>
          <span>,</span>
          {"\n\t  "}
          <span>languageCode: </span>
          <LanguageDropdown value={selectedLanguage} onChange={onLanguageChange} options={[...LANGUAGE_OPTIONS]} />
          <span>,</span>
          <span style={{ color: "#8A8A8A" }}>{` // ${t("setLanguage")}`}</span>
          {"\n\t  "}
          <span>diarize: </span>
          <span style={{ color: "#0A59D2" }}>true</span>
          {"\n\t"}
          <span>{"});"}</span>
          {"\n\n"}
          <span>console.</span>
          <span style={{ color: "#0A59D2" }}>log</span>
          <span>(transcription);</span>
        </code>
      </pre>
      {/* eslint-enable i18next/no-literal-string */}
    </CodeCardWrapper>
  );
}

// Code card for realtime transcription variant with useScribe hook
function RealtimeTranscriptionCodeCard({
  title,
  selectedLanguage,
  onLanguageChange,
}: {
  title?: string;
  selectedLanguage: string;
  onLanguageChange: (code: string) => void;
}) {
  const t = useTranslations("ClientKeys.TTS");
  return (
    <CodeCardWrapper title={title}>
      {/* eslint-disable i18next/no-literal-string */}
      <pre className='tw-text-xs tw-leading-6 tw-font-mono tw-whitespace-pre-wrap tw-break-words tw-text-black'>
        <code>
          <span style={{ color: "#F41A2F" }}>import</span>
          <span>{" { "}</span>
          <span>useScribe</span>
          <span>{" } "}</span>
          <span style={{ color: "#F41A2F" }}>from</span>
          <span style={{ color: "#8A8A8A" }}>{' "@elevenlabs/react"'}</span>
          <span>;</span>
          {"\n\n"}
          <span style={{ color: "#F41A2F" }}>const</span>
          <span> scribe = </span>
          <span style={{ color: "#0A59D2" }}>useScribe</span>
          <span>({"{"}</span>
          {"\n  "}
          <span>modelId: </span>
          <span style={{ color: "#8A8A8A" }}>{'"scribe_v2_realtime"'}</span>
          <span>,</span>
          {"\n\n  "}
          <span>languageCode: </span>
          <LanguageDropdown value={selectedLanguage} onChange={onLanguageChange} options={[...LANGUAGE_OPTIONS]} />
          <span>,</span>
          <span style={{ color: "#8A8A8A" }}>{` // ${t("setLanguage")}`}</span>
          {"\n\n  "}
          <span>onSessionStarted: </span>
          <span style={{ color: "#F41A2F" }}>{"() =>"}</span>
          {"\n    "}
          <span>console.</span>
          <span style={{ color: "#0A59D2" }}>log</span>
          <span>(</span>
          <span style={{ color: "#8A8A8A" }}>{'"Session started"'}</span>
          <span>),</span>
          {"\n  "}
          <span>onPartialTranscript: </span>
          <span style={{ color: "#F41A2F" }}>{"(data) =>"}</span>
          {"\n    "}
          <span>console.</span>
          <span style={{ color: "#0A59D2" }}>log</span>
          <span>(</span>
          <span style={{ color: "#8A8A8A" }}>{'"Partial:"'}</span>
          <span>, data.text)</span>
          {"\n"}
          <span>{"});"}</span>
        </code>
      </pre>
      {/* eslint-enable i18next/no-literal-string */}
    </CodeCardWrapper>
  );
}

// Extended type until Payload types are regenerated
type CodeLanguageGlobeBlockExtended = CodeLanguageGlobeBlock & {
  languageAudioSamples?: Array<{
    locale?: string;
    audio?: Media | string;
  }> | null;
};

export function BlockCodeLanguageGlobe({
  title,
  variant = "transcription",
  languageAudioSamples: cmsAudioSamples,
}: CodeLanguageGlobeBlockExtended) {
  const [selectedLanguage, setSelectedLanguage] = useState("eng");

  const selectedOption = useMemo(() => {
    return LANGUAGE_OPTIONS.find(opt => opt.code === selectedLanguage) || LANGUAGE_OPTIONS[0];
  }, [selectedLanguage]);

  // Process CMS audio samples into the format expected by Globe
  const languageAudioSamples = useMemo((): LanguageAudioSample[] => {
    if (!cmsAudioSamples) return [];
    return cmsAudioSamples.flatMap(sample => {
      const audioUrl = getAudioUrl(sample.audio as Media);
      if (!audioUrl || !sample.locale) return [];
      return [{ locale: sample.locale, audioUrl }];
    });
  }, [cmsAudioSamples]);

  const CodeCardComponent =
    variant === "realtime-transcription" ? RealtimeTranscriptionCodeCard : TranscriptionCodeCard;

  return (
    <Block name='code-language-globe' className='container'>
      <div className='tw-flex tw-flex-col lg:tw-flex-row'>
        {/* Left: Code card */}
        <div className='tw-flex-1 tw-min-w-0'>
          <CodeCardComponent
            title={title || undefined}
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
          />
        </div>

        {/* Vertical separator */}
        <div
          className='tw-relative tw-h-[--divider] tw-w-full lg:tw-h-auto lg:tw-w-[--divider] tw-my-6 lg:tw-my-0 lg:tw-mx-8'
          style={{ "--divider": "0.0625rem" } as React.CSSProperties}
        />

        {/* Right: Globe with centered language label */}
        <div className='tw-flex-1 tw-min-w-0 tw-flex tw-items-center tw-justify-center tw-py-8 lg:tw-py-0'>
          <div
            className='tw-relative tw-size-[--globe-size] md:tw-size-[--globe-size-md] lg:tw-size-[--globe-size-lg]'
            style={
              {
                "--globe-size": "18.75rem",
                "--globe-size-md": "23.75rem",
                "--globe-size-lg": "28.125rem",
              } as React.CSSProperties
            }
          >
            <LazyGlobe selectedLocale={selectedOption.locale} languageAudioSamples={languageAudioSamples} />
          </div>
        </div>
      </div>
    </Block>
  );
}
